// Convert quick-add text (task-line syntax) into a CreateItem payload. Client-side only for preview;
// the server remains the authority on parsing document task lines.
import type { Actor, Bucket, CreateItem, Priority, Status } from "../api/types";
import { parseQuickAdd, type TaskLine } from "../editor/tasktokens";
import { matchBucket } from "./bucketRemote";
import { matchActorByHandle, tokenDateToIso } from "./format";

export interface QuickAddResult {
  parsed: TaskLine;
  create: CreateItem;
  /** Tokens that could not be mapped (unknown @handle, bad date, unknown status). */
  warnings: string[];
}

export function quickAddToCreate(
  text: string,
  opts: { statuses?: Status[]; actors?: Actor[]; defaultStatusId?: string; buckets?: Bucket[] } = {},
): QuickAddResult {
  const parsed = parseQuickAdd(text.trim());
  const warnings: string[] = [];
  const create: CreateItem = { title: parsed.title || text.trim() };

  if (parsed.priority !== null) create.priority = parsed.priority as Priority;
  if (parsed.due) {
    const d = tokenDateToIso(parsed.due);
    if (d) {
      create.due_at = d.iso;
      create.due_has_time = d.hasTime;
    } else warnings.push(`Unrecognized due date "${parsed.due}"`);
  }
  if (parsed.start) {
    const d = tokenDateToIso(parsed.start);
    if (d) create.start_at = d.iso;
    else warnings.push(`Unrecognized start date "${parsed.start}"`);
  }
  if (parsed.labels.length) create.labels = [...new Set(parsed.labels)];
  if (parsed.people.length) {
    const ids: string[] = [];
    for (const h of parsed.people) {
      const a = opts.actors ? matchActorByHandle(opts.actors, h) : undefined;
      if (a) ids.push(a.id);
      else warnings.push(`Unknown person @${h}`);
    }
    if (ids.length) create.assignees = [...new Set(ids)];
  }
  let statusId = opts.defaultStatusId;
  if (parsed.status && opts.statuses) {
    const s = parsed.status.toLowerCase();
    const st =
      opts.statuses.find((x) => x.name.toLowerCase() === s) ??
      opts.statuses.find((x) => x.category === s.replace(/[\s-]/g, "_"));
    if (st) statusId = st.id;
    else warnings.push(`Unknown status "${parsed.status}"`);
  }
  if (statusId) create.status_id = statusId;
  if (parsed.bucket && opts.buckets) {
    const b = matchBucket(opts.buckets, parsed.bucket);
    if (b) create.bucket_id = b.id;
    else warnings.push(`Unknown bucket "${parsed.bucket}"`);
  }
  return { parsed, create, warnings };
}
